import type { PlaceSnapshot } from '@/types/gmb';
import { formatDateTime } from '@/pages/gmb-audit/utils/format';

/**
 * UI-2A｜Report Identity Header（報告最上方）。
 * 只顯示公開可理解欄位：店家名稱 / 主要分類 / 地址或服務區域 / 健診日期 / 同業樣本數。
 * 不顯示 schemaVersion / scoringModelVersion / requestId / internal id 等技術資訊（留方法說明區）。
 */
export default function ReportIdentityHeader({
  target,
  reportDate,
  peerSampleCount,
}: {
  target: PlaceSnapshot;
  reportDate: string;
  peerSampleCount?: number | null;
}) {
  const name = target.displayName?.trim() || '未命名商家';
  const typeName = target.primaryTypeDisplayName?.trim();
  const address = target.formattedAddress?.trim();
  const isServiceArea = target.pureServiceAreaBusiness === true;
  const sampleText = typeof peerSampleCount === 'number' ? `${peerSampleCount} 家` : '資料不足';

  return (
    <section className="ds-container pt-10 md:pt-14">
      <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-7">
        <p className="ds-badge bg-primary-50 text-primary-800 ring-1 ring-primary-200">
          <i className="ri-file-shield-2-line" />
          健診報告
        </p>

        <h2 className="mt-4 text-2xl leading-tight tracking-[-0.01em] md:text-3xl">{name}</h2>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 text-base text-foreground-600">
          {typeName && (
            <span className="inline-flex items-center gap-2">
              <i className="ri-price-tag-3-line text-lg text-primary-700" />
              {typeName}
            </span>
          )}
          {isServiceArea ? (
            <span className="ds-badge bg-accent-100 text-accent-800 ring-1 ring-accent-200">
              <i className="ri-service-line" />
              服務區域商家
            </span>
          ) : address ? (
            <span className="inline-flex items-center gap-2">
              <i className="ri-map-pin-line text-lg text-primary-700" />
              {address}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 text-foreground-500">
              <i className="ri-map-pin-line text-lg" />
              此商家未公開實體地址
            </span>
          )}
        </div>

        <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3 border-t border-background-200 pt-4">
          <div className="inline-flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-calendar-line text-base" />
            <dt>本次健診日期</dt>
            <dd className="font-semibold text-foreground-800">{formatDateTime(reportDate)}</dd>
          </div>
          <div className="inline-flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-group-line text-base" />
            <dt>同業樣本數</dt>
            <dd className="font-semibold text-foreground-800">{sampleText}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}